import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AccordionItem, Button } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import React from 'react';
import { ContactSupportButtons } from '../../../../../../../../dashboard/pages/XRayOnboarding/InstallationPage/ContactSupportButtons/ContactSupportButtons';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import styles from './ButtonClickEvents.module.css';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';

export const ButtonClickEvents = ({ promoterId }: { promoterId: number }) => {
  const promoter = useSelector((state: StoreState) =>
    state.promoter.entities.find(
      promoter => promoter.id?.toString() === promoterId?.toString(),
    ),
  );
  const promoterJSCode = `<script>
window.xrayDataLayer = window.xrayDataLayer || [];
function xrayData() { xrayDataLayer.push(arguments); }
</script>`;

  return (
    <BaseIntegration
      className={styles.integration}
      accorditionItemProps={{
        caretStyleIcon: true,
        showArrow: true,
        canToggle: true,
      }}
      titleComponent={
        <BaseIntegrationHeader
          title="Advanced Event Setup"
          subtitle="Write custom events into the customer journey or send custom attributes to the contact."
          css={{ container: styles.integrationsHeader }}
          landmineName={landmineNames.BUTTON_CLICK_EVENTS}
        />
      }
      showEmptyState={!promoter}
    >
      <div className={styles.content}>
        <div className={styles.alert}>
          <InfoCircleOutlined className={styles.infoIcon} />
          It takes a few minutes before events show up in a contact's customer
          journey.
        </div>
        <div className={styles.block}>
          <div>
            <div className={styles.title}>
              1. Add the script on your website
            </div>
            Add the following code below the existing{' '}
            <span className={styles.redText}>{'<script>'}</span> tag that
            includes the X-Ray Website Script
          </div>

          <textarea
            readOnly
            className={classNames(styles.codeBlock, styles.code)}
            value={promoterJSCode}
            onFocus={e => e.target.select()}
          />
          <CopyToClipboard text={promoterJSCode}>
            <Button
              leftIcon={<CopyOutlined />}
              css={{ container: styles.codeBlockCopyButton }}
            >
              Copy Code
            </Button>
          </CopyToClipboard>
        </div>
        <AccordionItem
          className={styles.block}
          caretStyleIcon
          openOnMount={false}
          closeOnOutsideClick={false}
          title={
            <div className={styles.section}>
              How to customize the customer journey's code
            </div>
          }
        >
          <div className={styles.accordionContent}>
            <div>
              In an event handler (e.g., the click event on a button), you can
              trigger a customer journey events by making a call to the{' '}
              <span className={styles.redText}>xrayData</span> method with the{' '}
              <span className={styles.redText}>page_view</span> event type
              together with the desired URL.
            </div>
            <div className={styles.alert}>
              <InfoCircleOutlined className={styles.infoIcon} />
              The code will need to be customized by a web developer to suit the
              layout of your site.
            </div>
            <div className={styles.title}>Examples:</div>
            <div className={styles.codeBlock}>
              {`document.querySelector('button.add-to-cart').addEventListener('click', function () 
            { xrayData('page_view', 'https://example.org/use-a-unique-url-here') });`}
            </div>
            <div>
              In this example, we are adding the trigger to an HTML button with
              the <span className={styles.redText}>add-to-cart</span> CSS class.
              (e.g.,{' '}
              <span className={styles.redText}>
                {'<button class=”add-to-cart”>Add to Cart</button>'}
              </span>
              )
            </div>
            <div className={styles.codeBlock} style={{ marginTop: 20 }}>
              {`<button onclick="xrayData('page_view', 'https://example.org/use-a-unique-url-here')”>Add to Cart</button>`}
            </div>
            <div>
              In this example, we are customizing the HTML button to add an{' '}
              <span className={styles.redText}>onclick</span> handler, which
              triggers a customer journey even when the user clicks on the
              button.
            </div>
          </div>
        </AccordionItem>

        <AccordionItem
          className={styles.block}
          caretStyleIcon
          openOnMount={false}
          closeOnOutsideClick={false}
          title={
            <div className={styles.section}>
              How to customize the contacts attributes
            </div>
          }
        >
          <div className={styles.accordionContent}>
            <div>
              To set an attribute, use the{' '}
              <span className={styles.redText}>set_attribute</span> method of
              the Xray API, where the two parameters can be any desired
              attribute name and value, such as:
            </div>
            <div className={styles.codeBlock}>
              {`xrayData('set_attribute', 'attribute_name', 'attribute_value')`}
            </div>
            <div className={styles.alert}>
              <InfoCircleOutlined className={styles.infoIcon} />
              The code will need to be customized by a web developer to suit the
              layout of your site.
            </div>
            <div className={styles.title} style={{ marginTop: '28px' }}>
              Payload Example:
              <div className={styles.text}>
                You can customize however you want.
              </div>
            </div>
            <div className={styles.codeExample}>
              <span>Headers:</span>
              <div className={styles.codeBlock}>
                {`{
  "token": "12312lk3j1lk2j3kl1j2lk31kl2j3kj",
  "other_header": "value",
  "Authorization": "Basic am9obkBleGFtcGxlLmNvbTphYmMxMjM="
}`}
              </div>
            </div>
            <div className={styles.codeExample}>
              <span>Bodies:</span>
              <div className={styles.codeBlock}>
                {`{
  "squadName": "Super hero squad",
  "homeTown": "Metro City",
  "formed": 2016,
  "secretBase": "Super tower",
  "active": true
}`}
              </div>
            </div>
          </div>
        </AccordionItem>
        {promoter && (
          <ContactSupportButtons
            promoter={promoter}
            className={styles.supportButtons}
            advanced
          />
        )}
      </div>
    </BaseIntegration>
  );
};
